import styled from "styled-components";
import { tokens } from "../../../../public/tokens";

export const StyledTableScroller = styled.div({
    width: "100%",
    overflowX: "auto",

    border: "0 !important",
    outline: "0 !important",
    boxShadow: "none !important",
});

export const StyledTable = styled.table(({ $qaView }) => ({
    width: "100%",

    minWidth: $qaView ? "1322px" : "1312px",

    tableLayout: "fixed",
    borderCollapse: "collapse",
    borderSpacing: 0,

    border: "0 !important",
    outline: "0 !important",
    boxShadow: "none !important",

    color: tokens.colors.text.primary,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],

    "tbody tr:last-child td": {
        borderBottom: "0 !important",
    },

    "thead, tbody, tr": {
        borderLeft: "0 !important",
        borderRight: "0 !important",
        outline: "0 !important",
    },
}));

export const StyledHeadCell = styled.th({
    height: "34px",

    padding: `0 ${tokens.globals["spacing-4"]} ${tokens.globals["spacing-1"]}`,

    borderTop: "0 !important",
    borderLeft: "0 !important",
    borderRight: "0 !important",
    borderBottom: `1px solid ${tokens.colors.surfaces["border-divider"]} !important`,

    outline: "0 !important",

    background: tokens.colors.surfaces["container-background"],

    textAlign: "left",
    verticalAlign: "middle",

    color: tokens.colors.neutral.grey["800"],

    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: "20px",

    whiteSpace: "nowrap",

    boxSizing: "border-box",
});

export const StyledCheckboxHeadCell = styled(StyledHeadCell)({
    width: "49px",

    paddingLeft: 0,
    paddingRight: 0,

    textAlign: "center",

    "> *": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
});

/*
 * This is the one column left flexible under the table's fixed layout, so it
 * (not blank trailing space) absorbs whatever room is left over once every
 * other column has taken its fixed width.
 */
export const StyledProgrammeHeadCell = styled(StyledHeadCell)({
    paddingLeft: tokens.globals["spacing-2"],
});

export const StyledVersionHeadCell = styled(StyledHeadCell)({
    width: "101px",
});

export const StyledIntakeHeadCell = styled(StyledHeadCell)({
    width: "93.5px",
});

export const StyledDateHeadCell = styled(StyledHeadCell)({
    width: "105.5px",
});

export const StyledEndDateHeadCell = styled(StyledHeadCell)({
    width: "98.5px",
});

export const StyledGradesHeadCell = styled(StyledHeadCell)({
    width: "239px",
});

export const StyledStudentsHeadCell = styled(StyledHeadCell)({
    width: "72.5px",
    textAlign: "center",
});

export const StyledActionsHeadCell = styled(StyledHeadCell)({
    width: "112px",
});

export const StyledCell = styled.td({
    height: "56px",

    padding: `${tokens.globals["spacing-2"]} ${tokens.globals["spacing-4"]}`,

    borderTop: "0 !important",
    borderLeft: "0 !important",
    borderRight: "0 !important",
    borderBottom: `1px solid ${tokens.colors.surfaces["border-divider"]} !important`,

    outline: "0 !important",

    verticalAlign: "middle",

    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "20px",

    boxSizing: "border-box",
});

export const StyledCheckboxCell = styled(StyledCell)({
    width: "49px",

    paddingLeft: 0,
    paddingRight: 0,

    textAlign: "center",

    "> *": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
});

export const StyledProgrammeCell = styled(StyledCell)({
    paddingLeft: tokens.globals["spacing-2"],
});

export const StyledStudentCell = styled(StyledCell)({
    textAlign: "center",
});

export const StyledSortButton = styled.button({
    padding: 0,
    border: 0,

    background: "transparent",
    color: "inherit",

    font: "inherit",

    display: "inline-flex",
    alignItems: "center",
    gap: "10px",

    cursor: "pointer",

    "&:focus-visible": {
        outline: `1px solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: "2px",
    },

    "&:disabled": {
        cursor: "not-allowed",
    },
});

export const StyledSortIcon = styled.i({
    width: "12.5px",
    height: "12.5px",

    flex: "0 0 12.5px",

    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    color: tokens.colors.neutral.black,

    fontSize: "10px",
    lineHeight: "10px",

    textAlign: "center",
});

export const StyledHeaderIcon = styled.i({
    width: "14px",
    flex: "0 0 14px",

    marginRight: "-4px",

    color: tokens.colors.neutral.grey["800"],

    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: tokens.typography.fontSize["lg-14px"],

    textAlign: "center",
});

/*
 * Programme name + campus/method meta cluster is shared with ModuleReadinessPopover's
 * header via linkedProgrammes.styles.js — both render the same identity block.
 */
export {
    StyledProgrammeDetails,
    StyledProgrammeName,
    StyledProgrammeNameLink,
    StyledProgrammeMetaRow,
    StyledMethod,
} from "./linkedProgrammes.styles";

export const StyledVersionValue = styled.span({
    display: "inline-flex",
    alignItems: "center",

    gap: tokens.globals["spacing-1"],

    color: tokens.colors.brand.blue["500"],

    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.bold,
    lineHeight: "23px",

    i: {
        fontSize: "12px",
    },
});

export const StyledVersionActiveIcon = styled.i({
    color: tokens.colors.states.success["500"],
});

export const StyledVersionInactiveIcon = styled.i({
    color: tokens.colors.neutral.grey["400"],
});

export const StyledGradeCellContent = styled.div({
    width: "100%",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    gap: tokens.globals["spacing-3"],
});

export const StyledGradeStatus = styled.div({
    minWidth: 0,

    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",

    gap: tokens.globals["spacing-0-5"],

    overflow: "hidden",
});

export const StyledGradeBadge = styled.span(({ $locked }) => ({
    minHeight: "18px",
    maxHeight: "18px",

    padding: `${tokens.globals["spacing-1"]} ${tokens.globals["spacing-2"]}`,

    borderRadius: "10px",

    background: $locked
        ? tokens.colors.brand.naartjie["100"]
        : tokens.colors.neutral.grey.Alpha["10"],

    color: $locked
        ? tokens.colors.states.warning["500"]
        : tokens.colors.text.secondary,

    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    gap: "4px",

    fontFamily:
        tokens.typography.fontFamilies["albert-sans"],
    fontSize:
        tokens.typography.fontSize["sm-10px"],
    fontWeight:
        tokens.typography.fontWeights.semibold,

    lineHeight: 1,

    whiteSpace: "nowrap",

    i: {
        fontSize: "8px",
    },
}));

export const StyledGradeMeta = styled.span({
    maxWidth: "150px",

    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",

    color: tokens.colors.neutral.grey["500"],

    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["xs-8px"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "normal",
});

export const StyledGradeAction = styled.button(({ $action }) => ({
    width: "35px",
    height: "35px",

    padding: 0,

    border: `1px solid ${tokens.colors.surfaces["border-divider"]}`,
    borderRadius: tokens.globals["border-radius"],

    background:
        tokens.colors.surfaces["container-background"],

    boxShadow:
        tokens.effects.effects.shadow.default,

    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    flexShrink: 0,

    cursor: "pointer",

    color:
        $action === "loading"
            ? tokens.colors.brand.blue["500"]
            : $action === "lock"
                ? tokens.colors.states.warning["500"]
                : tokens.colors.neutral.grey["600"],

    i: {
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: tokens.typography.fontSize["lg-14px"],
    },

    "&:hover:not(:disabled)": {
        background:
            tokens.colors.neutral.grey["200"],
    },

    "&:focus-visible": {
        outline: `1px solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: "2px",
    },

    "&:disabled": {
        opacity: 0.5,
        cursor: "not-allowed",
    },
}));

export const StyledLoadingRow = styled.td({
    height: "112px",

    padding: tokens.globals["spacing-6"],

    borderTop: "0 !important",
    borderLeft: "0 !important",
    borderRight: "0 !important",
    borderBottom: `1px solid ${tokens.colors.surfaces["border-divider"]} !important`,

    textAlign: "center",

    color: tokens.colors.text.secondary,

    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
});

/*
 * This is the one column left flexible under the table's fixed layout, so it
 * (not blank trailing space) absorbs whatever room is left over once every
 * other column has taken its fixed width.
 */
export const StyledQaProgrammeHeadCell = styled(StyledHeadCell)({
    paddingLeft: tokens.globals["spacing-2"],
});

export const StyledQaProgrammeCell = styled(StyledCell)({
    paddingLeft: tokens.globals["spacing-2"],
});

export const StyledQaVersionHeadCell = styled(StyledHeadCell)({
    width: "101px",
});

export const StyledQaVersionCell = styled(StyledCell)({
    width: "101px",
});

export const StyledQaIntakeHeadCell = styled(StyledHeadCell)({
    width: "93.5px",
});

export const StyledQaIntakeCell = styled(StyledCell)({
    width: "93.5px",
});

export const StyledReadinessHeadCell = styled(StyledHeadCell)({
    width: "373px",
    paddingLeft: tokens.globals["spacing-2"],
});

export const StyledReadinessCell = styled(StyledCell)({
    width: "373px",
    padding: tokens.globals["spacing-2"],
});

export const StyledQaGradesHeadCell = styled(StyledHeadCell)({
    width: "213px",
});

export const StyledQaGradesCell = styled(StyledCell)({
    width: "213px",
    padding: `${tokens.globals["spacing-2"]} 19px`,
});

export const StyledQaStudentsHeadCell = styled(StyledHeadCell)({
    width: "72.5px",
    textAlign: "center",
});

export const StyledQaStudentsCell = styled(StyledCell)({
    width: "72.5px",
    textAlign: "center",
});

export const StyledQaActionsHeadCell = styled(StyledHeadCell)({
    width: "104px",
    paddingLeft: tokens.globals["spacing-2"],
});

export const StyledQaActionsCell = styled(StyledCell)({
    width: "104px",
    padding: `${tokens.globals["spacing-2"]} ${tokens.globals["spacing-4"]} ${tokens.globals["spacing-2"]} ${tokens.globals["spacing-2"]}`,
});

export const StyledReadinessRow = styled.div({
    width: "100%",
    minWidth: 0,

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    gap: tokens.globals["spacing-3"],
});

export const StyledReadinessState = styled.div({
    minWidth: 0,

    display: "flex",
    alignItems: "flex-start",

    gap: tokens.globals["spacing-1-5"],
});

export const StyledReadinessStateIcon = styled.i(({ $ready }) => ({
    width: "14px",
    height: "14px",
    flex: "0 0 14px",
    marginTop: $ready ? "0" : "1px",

    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    color: $ready
        ? tokens.colors.states.success["500"]
        : tokens.colors.ui.destructive,

    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: tokens.typography.fontSize["lg-14px"],
}));

export const StyledReadinessText = styled.div({
    minWidth: 0,

    display: "flex",
    flexDirection: "column",
    justifyContent: "center",

    gap: tokens.globals["spacing-0-5"],
});

export const StyledReadinessCount = styled.span({
    color: tokens.colors.text.primary,

    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: 1.1,

    whiteSpace: "nowrap",
});

export const StyledReadinessWarnings = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2"],

    flexWrap: "wrap",
});

export const StyledReadinessWarning = styled.span({
    display: "inline-flex",
    alignItems: "center",

    gap: tokens.globals["spacing-0-5"],

    color: tokens.colors.text.secondary,

    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["sm-10px"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "12px",

    whiteSpace: "nowrap",

    i: {
        width: "8px",
        height: "8px",

        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",

        fontSize: "8px",
        lineHeight: "8px",
    },
});

export const StyledModulesButton = styled.button(({ $active }) => ({
    width: "94px",
    height: "35px",
    flex: "0 0 94px",

    padding: `0 ${tokens.globals["spacing-2-5"]}`,

    border: `1px solid ${
        $active
            ? tokens.colors.neutral.black
            : tokens.colors.surfaces["border-divider"]
    }`,

    borderRadius: tokens.globals["border-radius"],

    background: $active
        ? tokens.colors.neutral.black
        : tokens.colors.neutral.white,

    color: $active
        ? tokens.colors.neutral.white
        : tokens.colors.text.primary,

    boxShadow: tokens.effects.effects.shadow.default,

    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    gap: `${tokens.globals["spacing-1-5"]}`,

    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.semibold,
    lineHeight: "20px",

    cursor: "pointer",

    i: {
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: tokens.typography.fontSize["lg-14px"],
    },

    "&:hover:not(:disabled)": {
        background: $active
            ? tokens.colors.neutral.grey["800"]
            : tokens.colors.neutral.grey["200"],
    },

    "&:focus-visible": {
        outline: `1px solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: "2px",
    },

    "&:disabled": {
        opacity: 0.5,
        cursor: "not-allowed",
    },
}));
